import { Description } from '@mui/icons-material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  deleteVerification,
  downloadVerificationFile,
  getDocumentVerificationsQuery,
  getVerificationFileAsBlobQuery,
} from 'lib/api/documentConfirmation';
import type {
  DocumentVerificationItem,
  DocumentVerificationsResponse,
} from 'lib/api/documentConfirmation/types';
import DocumentsPreviewer from 'lib/common/DocumentsPreviewer';
import CustomButton from 'lib/designSystem/Button';
import TableCard from 'lib/designSystem/Card/TableCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SmallHeaderWithIcon from 'lib/designSystem/SmallHeaderWithIcon';
import EnhancedTable from 'lib/designSystem/Table';
import { formatDate } from 'lib/functions/utils';
import { formatMoney, showAlert } from 'lib/functions/utils';
import { SummaryRow } from 'pages/Finance/Components/Financing/common/Summary';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

const DEFAULT_DOCUMENT_DETAILS: DocumentVerificationsResponse = {
  id: '',
  stakeholderName: '',
  businessName: '',
  amount: 0,
  folio: '',
  documentConfirmation: [],
};

const DocumentConfirmationDetailsModule = (props: any) => {
  const { document, open, setOpen } = props;

  const dispatch = useDispatch();

  const {
    data: documentDetails = DEFAULT_DOCUMENT_DETAILS,
    isPending: loading,
  } = getDocumentVerificationsQuery(dispatch, document?.id ?? '', {
    enabled: open && !!document?.id,
  });

  const deleteVerificationMutation = deleteVerification(dispatch);
  const { isPending: isDeletingVerification } = deleteVerificationMutation;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFileKey, setPreviewFileKey] = useState<string>('');
  const [previewFileName, setPreviewFileName] = useState<string | null>(null);
  const [previewConfirmationId, setPreviewConfirmationId] = useState<
    number | null
  >(null);

  const {
    data: previewBlobData,
    isPending: previewLoading,
    error: previewQueryError,
  } = getVerificationFileAsBlobQuery(dispatch, previewFileKey, {
    enabled: previewOpen && !!previewFileKey,
  });

  // Simply indicate if there's an error (DocumentsPreviewer handles the display)
  const previewError = previewQueryError ? 'Error' : null;

  const previewBlobUrl = useMemo(() => {
    if (previewBlobData?.blob) {
      return URL.createObjectURL(previewBlobData.blob);
    }
    return null;
  }, [previewBlobData?.blob]);

  useEffect(() => {
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
      }
    };
  }, [previewBlobUrl]);

  const handleDownloadFile = async (confirmationId: number) => {
    try {
      const confirmation = (documentDetails?.documentConfirmation ?? []).find(
        (c: DocumentVerificationItem) => c.id === confirmationId,
      );
      const response = await downloadVerificationFile(
        dispatch,
        confirmation?.file ?? '',
      );
      const url = response.url;
      window.open(url, '_blank');
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Ha ocurrido un error al descargar el archivo',
      );
    }
  };

  const handlePreviewFile = (confirmationId: number) => {
    const confirmation = (documentDetails?.documentConfirmation ?? []).find(
      (c: DocumentVerificationItem) => c.id === confirmationId,
    );

    if (!confirmation?.file) {
      showAlert(
        dispatch,
        'error',
        'No se encontró el archivo para previsualizar',
      );
      return;
    }

    setPreviewConfirmationId(confirmationId);
    setPreviewFileKey(confirmation.file);
    setPreviewFileName(
      `Verificación ${confirmation.type} - ${confirmation.id}`,
    );
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewFileKey('');
    setPreviewFileName(null);
    setPreviewConfirmationId(null);
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={() => setOpen(false)}>
      {loading ? (
        <Box
          sx={{
            minHeight: 280,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CenteredCircularProgress relative />
        </Box>
      ) : (
        <div>
          <DialogContent style={{ minWidth: '800px' }}>
            <Typography variant="h6" padding="16px 0px" fontWeight={600}>
              Verificación de documento
            </Typography>
            <Box sx={{ mb: 2 }}>
              <TableCard
                size="small"
                enabledEffects={false}
                sx={{
                  boxShadow: 'none',
                  background: 'transparent',
                  border: 'none',
                }}
              >
                <SmallHeaderWithIcon
                  icon={<Description fontSize="small" />}
                  title="Datos del documento"
                />
                <Grid container spacing={0} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Box sx={{ pr: 2 }}>
                      <SummaryRow
                        label="Cliente"
                        value={document.businessName}
                        labelVariant="body2"
                        valueVariant="body2"
                        valueMaxLength={30}
                      />
                      <SummaryRow
                        label="Deudor"
                        value={document.stakeholderName}
                        labelVariant="body2"
                        valueVariant="body2"
                        valueMaxLength={30}
                      />
                      <SummaryRow
                        label="Confirmada SII"
                        value={
                          document.confirmDate
                            ? formatDate(document.confirmDate, 'dd-mm-yyyy')
                            : 'No'
                        }
                        labelVariant="body2"
                        valueVariant="body2"
                        showBorder={false}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        pl: 2,
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.75,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography
                          fontWeight={600}
                          variant="body2"
                          color="text.secondary"
                        >
                          Folio
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                          {document.folio}
                        </Typography>
                      </Box>
                      <SummaryRow
                        label="Monto documento"
                        value={formatMoney(document.amount)}
                        labelVariant="body2"
                        valueVariant="body2"
                        showBorder={false}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </TableCard>
            </Box>
            <TableCard
              size="small"
              enabledEffects={false}
              sx={{
                boxShadow: 'none',
                background: 'transparent',
                border: 'none',
              }}
            >
              <Box>
                <SmallHeaderWithIcon
                  icon={<Description fontSize="small" />}
                  title="Historial de verificaciones manuales"
                />
                <EnhancedTable
                  data={documentDetails.documentConfirmation}
                  dataHeaders={[
                    {
                      key: 'type',
                      label: 'Tipo',
                    },
                    {
                      key: 'message',
                      label: 'Comentario',
                    },
                    {
                      key: 'confirmedBy',
                      label: 'Confirmada por',
                    },
                    {
                      key: 'contactName',
                      label: 'Contacto',
                    },
                    {
                      key: 'contactRole',
                      label: 'Cargo',
                    },
                    { key: 'createdAt', label: 'Fecha', type: 'date' },
                    {
                      key: 'hasFile',
                      label: 'Acciones',
                      type: 'custom',
                      customComponent: (
                        _value: unknown,
                        row: DocumentVerificationItem,
                      ) => {
                        return (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip
                              title={
                                row.hasFile
                                  ? 'Previsualizar archivo'
                                  : 'No se ha subido ningún documento'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!row.hasFile}
                                  onClick={() => {
                                    if (row.hasFile) {
                                      handlePreviewFile(row.id);
                                    }
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                row.hasFile
                                  ? 'Descargar archivo'
                                  : 'No se ha subido ningún documento'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!row.hasFile}
                                  onClick={() => {
                                    if (row.hasFile) {
                                      void handleDownloadFile(row.id);
                                    }
                                  }}
                                >
                                  <CloudDownloadIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                isDeletingVerification
                                  ? 'Eliminando...'
                                  : 'Eliminar verificación'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={isDeletingVerification}
                                  onClick={() => {
                                    deleteVerificationMutation.mutate(row.id);
                                  }}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        );
                      },
                    },
                  ]}
                  rowsPerPageDefault={5}
                  exportToFile="xlsx"
                />
              </Box>
            </TableCard>
          </DialogContent>
          <DialogActions>
            <CustomButton color="secondary" onClick={() => setOpen(false)}>
              Salir
            </CustomButton>
          </DialogActions>
        </div>
      )}
      <DocumentsPreviewer
        open={previewOpen}
        onClose={handleClosePreview}
        fileName={previewFileName ?? 'Archivo de verificación'}
        blobUrl={previewBlobUrl}
        contentType={previewBlobData?.contentType ?? null}
        loading={previewLoading}
        error={previewError}
        onDownload={() => {
          if (previewConfirmationId !== null) {
            void handleDownloadFile(previewConfirmationId);
          }
        }}
      />
    </Dialog>
  );
};

export default DocumentConfirmationDetailsModule;
